import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { useWeb3 } from '../context/Web3Context';
import { useLanguage } from '../context/LanguageContext';
import './LuckyDrawSection.css';

const weiToUsd = (wei) => {
  if (!wei || wei === '0') return '0.00';
  try {
    const n = Number(BigInt(wei)) / 1e18;
    return n.toFixed(2);
  } catch { return '0.00'; }
};

const LuckyDrawSection = () => {
  const { t } = useLanguage();
  const { isConnected } = useWeb3();
  const [draws, setDraws] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entering, setEntering] = useState(null);
  const [selectedDraw, setSelectedDraw] = useState(null);
  const [myEntries, setMyEntries] = useState([]);
  const [winners, setWinners] = useState([]);

  const fetchDraws = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/lucky-draw/draws');
      setDraws(res.data?.draws || []);
    } catch (e) {
      console.error('Failed to load draws', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDraws(); }, [fetchDraws]);

  const handleEnter = async (drawId) => {
    if (!isConnected) {
      return toast.error(t('connectWalletFirst'));
    }
    setEntering(drawId);
    try {
      const res = await api.post(`/lucky-draw/draws/${drawId}/enter`);
      toast.success(res.data?.message || t('entrySuccess'));
      fetchDraws();
      if (selectedDraw === drawId) loadMyEntries(drawId);
    } catch (e) {
      toast.error(e?.response?.data?.message || t('entryFailed'));
    } finally {
      setEntering(null);
    }
  };

  const loadMyEntries = async (drawId) => {
    if (!isConnected) return;
    try {
      const res = await api.get(`/lucky-draw/draws/${drawId}/my-entries`);
      setMyEntries(res.data?.entries || []);
    } catch (_) { /* */ }
  };

  const loadResults = async (drawId) => {
    try {
      const res = await api.get(`/lucky-draw/draws/${drawId}/results`);
      setWinners(res.data?.winners || []);
    } catch (_) { /* */ }
  };

  const handleSelect = (draw) => {
    const id = draw._id;
    setSelectedDraw(selectedDraw === id ? null : id);
    setMyEntries([]);
    setWinners([]);
    if (selectedDraw !== id) {
      loadMyEntries(id);
      if (draw.winnersSelected) loadResults(id);
    }
  };

  const now = new Date();

  if (loading) return <div className="ld-loading">{t('loading')}</div>;

  return (
    <div className="lucky-draw-section">
      {draws.length === 0 && <div className="ld-empty">{t('noActiveDraws')}</div>}

      {draws.map(d => {
        const isActive = d.status === 'active' && new Date(d.startsAt) <= now && new Date(d.endsAt) >= now;
        const isSelected = selectedDraw === d._id;
        const poolWei = BigInt(d.entryFeeWei || '0') * BigInt(d.totalEntries || 0);

        return (
          <div key={d._id} className={`ld-card ${isSelected ? 'ld-card-expanded' : ''}`}>
            <div className="ld-card-top" onClick={() => handleSelect(d)}>
              <div className="ld-card-info">
                <h3>{d.title}</h3>
                {d.description && <p className="ld-desc">{d.description}</p>}
              </div>
              <span className={`ld-status ld-status-${d.status}`}>{d.status}</span>
            </div>

            <div className="ld-card-stats">
              <div className="ld-stat">
                <span className="ld-stat-label">{t('entryFee')}</span>
                <span className="ld-stat-value">${d.entryFeeUsd} USDT</span>
              </div>
              <div className="ld-stat">
                <span className="ld-stat-label">{t('entries')}</span>
                <span className="ld-stat-value">{d.totalEntries || 0}{d.maxEntries > 0 ? ` / ${d.maxEntries}` : ''}</span>
              </div>
              <div className="ld-stat">
                <span className="ld-stat-label">{t('prizePool')}</span>
                <span className="ld-stat-value ld-gold">${weiToUsd(poolWei.toString())} USDT</span>
              </div>
              <div className="ld-stat">
                <span className="ld-stat-label">{t('endsAt')}</span>
                <span className="ld-stat-value">{new Date(d.endsAt).toLocaleDateString()}</span>
              </div>
            </div>

            {d.prizes && d.prizes.length > 0 && (
              <div className="ld-prizes">
                {d.prizes.map((p, i) => (
                  <div key={i} className="ld-prize-chip">
                    <span className="ld-prize-icon">
                      {p.type === 'pool' ? '$' : p.type === 'nft' ? '◆' : '%'}
                    </span>
                    <span>{p.label}</span>
                    <span className="ld-prize-detail">
                      {p.type === 'pool' ? `${p.value}% pool` : p.type === 'smmDiscount' ? `${p.value}% off` : `#${p.value}`}
                    </span>
                    {p.quantity > 1 && <span className="ld-prize-qty">x{p.quantity}</span>}
                  </div>
                ))}
              </div>
            )}

            {isActive && (
              <button
                className="ld-enter-btn"
                onClick={(e) => { e.stopPropagation(); handleEnter(d._id); }}
                disabled={entering === d._id}
              >
                {entering === d._id ? t('processing') : `${t('enterDraw')} - $${d.entryFeeUsd}`}
              </button>
            )}

            {isSelected && (
              <div className="ld-expanded">
                {myEntries.length > 0 && (
                  <div className="ld-my-entries">
                    <h4>{t('myEntries')}</h4>
                    {myEntries.map(e => (
                      <div key={e._id} className="ld-entry-row">
                        <span>#{e.entryNumber}</span>
                        {e.prizeWon?.type && (
                          <span className="ld-won-badge">{t('won')}: {e.prizeWon.label}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {d.winnersSelected && winners.length > 0 && (
                  <div className="ld-winners">
                    <h4>{t('winners')}</h4>
                    {winners.map((w, i) => (
                      <div key={i} className="ld-winner-row">
                        <span className="ld-mono">{w.walletAddress.slice(0, 6)}...{w.walletAddress.slice(-4)}</span>
                        <span>{w.prizeWon?.label || '—'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default LuckyDrawSection;
